import React, { useCallback } from 'react';
import { useCss } from 'react-use';
import cn from 'classnames';

import ICO_CLOSE from 'assets/images/close-button.svg';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';

import CN from './CategoryField.module.scss';

const L = logger('CategoryField'); // eslint-disable-line no-unused-vars

const CategoryField = ({
  isOnboarding,
  category,
  isSaved,
  expanded,
  errorMessage,
  onOpenHandler = alwaysNoop,
  onEditHandler = alwaysNoop,
  onDeleteHandler = alwaysNoop,
  onSaveHandler = alwaysNoop,
}) => {
  const bulletColor = useCss({ backgroundColor: category?.color });

  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const toggleContent = alwaysNoop;

  const handleDelete = useCallback(() => void onDeleteHandler(category), [category, onDeleteHandler]);
  const handleOpen = useCallback(() => void onOpenHandler(category), [category, onOpenHandler]);
  const handleSave = useCallback(() => void onSaveHandler(category), [category, onSaveHandler]);

  const handleEdit = useCallback(() => {
    onEditHandler(category);
    onOpenHandler(category);
  }, [category, onEditHandler, onOpenHandler]);

  return (
    <div
      data-bem="CategoryField"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {isSaved && (
        <div data-bem="CategoryField__alert" className="alert success animated fadeInDown">
          {t('composite.categoriesScreen.saved')}
          <span className="close">
            <img src={ICO_CLOSE} alt="Close modal" />
          </span>
        </div>
      )}

      <div
        data-bem="CategoryField__content"
        onClick={handleEdit}
        className={cn({
          [CN.panel]: true,
          [CN.hidden]: !expanded,
        })}
      >
        <div className={CN.container}>
          <div className={CN.dragIcon}>
            <Icon variant="drag" width="lg" />
          </div>
          <div
            className={cn({
              [CN.bullet]: true,
              [bulletColor]: bulletColor,
            })}
          />
          <div className={CN.categoryName}>{category.name}</div>
        </div>

        {onOpenHandler && (category?.services.length || !isOnboarding) ? (
          <div onClick={toggleContent} className={CN.buttonsContainer}>
            {expanded ? (
              <Button variant="slim" width="small" onClick={handleSave}>
                {t('common.buttons.doneUpp')}
              </Button>
            ) : (
              <div className={CN.buttonGap}>
                <Button variant="slim" width="small" onClick={handleEdit}>
                  {t('common.buttons.editUpp')}
                </Button>
              </div>
            )}

            {!expanded && (
              <Button variant="bin" onClick={handleDelete}>
                <Icon variant="delete" width="lg" />
              </Button>
            )}
          </div>
        ) : (
          // NOTE: when is onboarding only toggle without editing or any other changes
          <Button variant="slim" width="small" onClick={handleOpen}>
            {expanded ? t('common.buttons.hideUpp') : t('common.buttons.showUpp')}
          </Button>
        )}
      </div>
      {errorMessage && <div>{errorMessage}</div>}
    </div>
  );
};

export default CategoryField;
